/**
 * Ordenación de las entidades internas (actuaciones, accidentes y
 * comunicaciones) del informe combinado de Generic Order / Campaña.
 *
 * El usuario elige en el modal (`DownloadHistoricModal`) por qué fecha se
 * ordenan las hijas del informe: fecha de registro (`registerInitDate`, la que
 * introduce el usuario) o fecha del sistema (`creationDate`), en dirección
 * ascendente o descendente.
 *
 * Fichero PURO y sin dependencias: lo importan el ensamblado del informe
 * (`fetchCombinedReportData`) y el spec E2E que verifica el orden aplicado.
 */

export type ReportSortField = 'registerInitDate' | 'creationDate';
export type ReportSortDirection = 'asc' | 'desc';

export type ReportSortOption = {
  field: ReportSortField;
  direction: ReportSortDirection;
};

/** Default del modal: fecha de registro, más recientes primero. */
export const DEFAULT_REPORT_SORT: ReportSortOption = {
  field: 'registerInitDate',
  direction: 'desc',
};

type SortableReportChild = {
  registerInitDate?: string | Date | null;
  creationDate?: string | Date | null;
};

/**
 * Ordena las hijas del informe por la fecha elegida.
 *
 * - No muta la lista de entrada (devuelve una copia) y el sort es estable.
 * - Las fechas null/ausentes/inválidas van SIEMPRE al final, sea cual sea la
 *   dirección elegida.
 */
export function sortReportChildren<T extends SortableReportChild>(
  children: T[],
  field: ReportSortField,
  direction: ReportSortDirection
): T[] {
  const toTime = (item: T): number | null => {
    const raw = item[field];
    if (raw === null || raw === undefined || raw === '') return null;
    const time = new Date(raw).getTime();
    return Number.isNaN(time) ? null : time;
  };
  return [...children].sort((a, b) => {
    const timeA = toTime(a);
    const timeB = toTime(b);
    if (timeA === null && timeB === null) return 0;
    if (timeA === null) return 1; // null al final siempre
    if (timeB === null) return -1;
    return direction === 'asc' ? timeA - timeB : timeB - timeA;
  });
}

/** Tipos de hija que puede contener el informe combinado. */
export type ReportChildType = 'action' | 'accident' | 'communication';

/**
 * Ensambla la SECUENCIA final de hijas del informe combinado: los 3 tipos
 * (actuaciones, accidentes y comunicaciones) ENTRELAZADOS en un único hilo
 * cronológico, ordenado por la fecha y dirección elegidas en el modal — sin
 * bloques fijos por tipo. El informe pinta esta secuencia tal cual,
 * despachando la plantilla por `record.type`.
 *
 * El entrelazado es POR informe (se invoca una vez por Order padre); fechas
 * null/inválidas van al final en ambas direcciones (herencia de
 * `sortReportChildren`).
 */
export function buildReportChildrenSequence<
  T extends SortableReportChild & { record: { type: ReportChildType } },
>(
  children: T[],
  field: ReportSortField,
  direction: ReportSortDirection
): T['record'][] {
  // UNA sola ordenación sobre la mezcla de los 3 tipos. NUNCA trocear por tipo
  // y concatenar: eso reintroduce los bloques fijos (actuaciones → accidentes →
  // comunicaciones) que rompían el hilo cronológico del informe.
  return sortReportChildren(children, field, direction).map((c) => c.record);
}
